<template>
  <v-container fluid class="scrollable-container">
    <div class="content-wrapper d-flex align-center justify-center">
      <v-container style="max-width: 1024px" class="pa-0 text-left">
        <h1 class="text-h5 font-weight-bold mb-2">
          Your Indices on Demand report includes:
        </h1>
        <p class="text-body-1 text-medium-emphasis mb-6">
          An exploration of the English Indices of Deprivation 2025 for your
          area, including neighbourhood breakdowns for each of the domains and
          how deprivation is changing over time. Delivered instantly and giving
          you the insight you need for evidence-based decision making, your
          report contains:
        </p>

        <!-- Guidelines for usage -->
        <section class="report-section mb-6">
          <v-row align="start" no-gutters>
            <v-col cols="auto" class="pr-4">
              <div class="section-icon-container">
                <v-icon size="28">mdi-compass-outline</v-icon>
              </div>
            </v-col>
            <v-col>
              <h2 class="text-headline-medium mb-2">Guidelines for usage</h2>
              <p class="text-medium-emphasis">
                Clear guidance on how the IMD should be interpreted so that you
                can confidently apply the findings in practice
              </p>
            </v-col>
          </v-row>
        </section>

        <v-divider class="mb-6"></v-divider>

        <!-- Index of Multiple Deprivation -->
        <section class="report-section mb-6">
          <v-row align="start" no-gutters>
            <v-col cols="auto" class="pr-4">
              <div class="section-icon-container">
                <v-icon size="28">mdi-magnify</v-icon>
              </div>
            </v-col>
            <v-col>
              <h2 class="text-headline-medium mb-2">
                Index of Multiple Deprivation (IMD)
              </h2>
              <p class="text-medium-emphasis">
                Understand overall levels of deprivation using the IMD, with
                comparisons to national and regional benchmarks
              </p>
            </v-col>
          </v-row>
        </section>

        <v-divider class="mb-6"></v-divider>

        <!-- Domain Analysis -->
        <section class="report-section mb-6">
          <v-row align="start" no-gutters>
            <v-col cols="auto" class="pr-4">
              <div class="section-icon-container">
                <v-icon size="28">mdi-layers-outline</v-icon>
              </div>
            </v-col>
            <v-col>
              <h2 class="text-headline-medium mb-2">Domain Analysis</h2>
              <p class="text-medium-emphasis mb-4">
                See how your area performs across the seven domains, starting
                with a summary view at a glance, followed by in-depth analysis
                of each domain.
              </p>

              <!-- Domain items with checkmarks -->
              <div class="domain-items">
                <div
                  v-for="domain in domains"
                  :key="domain.title"
                  class="d-flex align-start mb-5"
                >
                  <v-icon color="#3D5769" size="24" class="mr-3 mt-1">
                    mdi-check
                  </v-icon>
                  <div>
                    <h3 class="text-body-semibold mb-1">
                      {{ domain.title }}
                    </h3>
                    <p class="text-medium-emphasis">
                      {{ domain.description }}
                    </p>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </section>

        <v-divider class="mb-6"></v-divider>

        <!-- Changes in deprivation -->
        <section class="report-section mb-6">
          <v-row align="start" no-gutters>
            <v-col cols="auto" class="pr-4">
              <div class="section-icon-container">
                <v-icon size="28">mdi-chart-timeline-variant</v-icon>
              </div>
            </v-col>
            <v-col>
              <h2 class="text-headline-medium mb-2">Changes in deprivation</h2>
              <p class="text-medium-emphasis">
                Track how relative deprivation has changed over time to spot
                persistent challenges and emerging trends
              </p>
            </v-col>
          </v-row>
        </section>

        <v-divider class="mb-6"></v-divider>

        <!-- Further reading -->
        <section class="report-section mb-6">
          <v-row align="start" no-gutters>
            <v-col cols="auto" class="pr-4">
              <div class="section-icon-container">
                <v-icon size="28">mdi-book-open-blank-variant-outline</v-icon>
              </div>
            </v-col>
            <v-col>
              <h2 class="text-headline-medium mb-2">Further reading</h2>
              <p class="text-medium-emphasis">
                Broaden your understanding with relevant resources curated by
                our team
              </p>
            </v-col>
          </v-row>
        </section>
      </v-container>
    </div>

    <v-app-bar location="bottom" rounded="0" class="bottom-bar">
      <v-btn
        variant="text"
        rounded="0"
        class="ml-4 d-none d-sm-flex"
        style="color: #3d5769"
        @click="$router.push({ path: '/purchase/imd-report' })"
        >GO BACK</v-btn
      >
      <v-spacer class="d-none d-sm-flex" />

      <!-- Mobile layout: price on one line, button below -->
      <div
        class="d-flex d-sm-none flex-column align-center justify-center flex-grow-1 pb-2"
      >
        <div class="d-flex align-center justify-center my-0 py-1">
          <span class="order-subtotal-label mr-2">ORDER SUBTOTAL</span>
          <span class="text-h6 font-weight-bold" style="color: #4d4d4d">
            £495.00
          </span>
        </div>
        <div class="d-flex justify-center px-4 w-100">
          <v-btn
            variant="elevated"
            @click="$router.push({ name: 'ImdSelectArea' })"
            rounded="0"
            class="continue-btn pa-3 flex-grow-1"
            color="#7ad6d9"
            >CONTINUE</v-btn
          >
        </div>
      </div>

      <!-- Desktop layout: spaced out -->
      <div class="text-center d-none d-sm-block">
        <div class="order-subtotal-label">ORDER SUBTOTAL</div>
        <div class="text-h6 font-weight-bold" style="color: #4d4d4d">
          £495.00
        </div>
      </div>
      <v-spacer class="d-none d-sm-flex" />
      <v-btn
        variant="elevated"
        @click="$router.push({ name: 'ImdSelectArea' })"
        rounded="0"
        color="#7ad6d9"
        class="continue-btn pa-3 mr-4 d-none d-sm-flex"
        style="width: 220px"
        >CONTINUE</v-btn
      >
    </v-app-bar>
  </v-container>
</template>

<script>
import { useDisplay } from "vuetify";

export default {
  name: "ReportOutline",
  data() {
    return {
      screenHeight: useDisplay().height,
      screenWidth: useDisplay().width,
      fixedButtonHeight: 0,
      bottomBarHeight: 0,
      domains: [
        {
          title: "Domain summary",
          description:
            "See at a glance how your area performs across each of the seven domains through visuals and maps, as well as a breakdown of the ten most deprived LSOAs",
        },
        {
          title: "Income Deprivation",
          description:
            "Discover where low income affects communities most, with additional focus on older people (IDAOPI) and children (IDACI)",
        },
        {
          title: "Employment Deprivation",
          description:
            "Understand where there are higher proportions of the population excluded from work due to unemployment, illness, disability or caring responsibilities. ",
        },
        {
          title: "Education, Skills and Training Deprivation",
          description:
            "See how educational disadvantage impacts both young people and adults, with a breakdown across the Children and Young People and Adult Skills sub-domains",
        },
        {
          title: "Health Deprivation and Disability",
          description:
            "Explore where poor health and disability are having most impact on quality of life",
        },
        {
          title: "Crime Domain",
          description:
            "Understand the risk of crime, covering violent offenses, burglary, theft and criminal damage",
        },
        {
          title: "Barriers to Housing and Services Domain",
          description:
            "Uncover areas where communities face physical or financial challenges in accessing housing or key local services",
        },
        {
          title: "Living Environment Domain",
          description:
            "Identify the communities facing environmental risks to health and wellbeing, such as poor housing conditions, low air quality and road traffic accidents",
        },
      ],
    };
  },
  computed: {
    bottomPadding() {
      // Add padding to account for the bottom bar height
      return `${this.bottomBarHeight + 32}px`; // 32px base padding + bottom bar height
    },
  },
  mounted() {
    // Send visit to analytics
    this.$axios.put("/send-analytics", {
      event: "on_demand_iod_overview_visits",
    });
    this.measureBottomBarHeight();
    window.addEventListener("resize", this.measureBottomBarHeight);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.measureBottomBarHeight);
  },
  methods: {
    measureBottomBarHeight() {
      this.$nextTick(() => {
        const bottomBar = document.querySelector(".bottom-bar");
        if (bottomBar) {
          this.bottomBarHeight = bottomBar.offsetHeight;
        }
      });
    },
  },
};
</script>

<style scoped>
.text-display-large {
  color: #4d4d4d;
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
}

.text-headline-medium {
  color: #4d4d4d;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
}

.text-body-semibold {
  color: #4d4d4d;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
}

.section-icon-container {
  display: flex;
  width: 54px;
  height: 54px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  color: #3d5769;
  background-color: #c3ddd5;
  flex-shrink: 0;
}

/* Scrollable container */
.scrollable-container {
  padding: 32px 16px 88px 16px;
  height: calc(100vh - 48px); /* Account for banner height */
  overflow-y: auto;
  overflow-x: hidden;
}

/* Remove container padding on extra small screens */
@media (max-width: 480px) {
  .scrollable-container {
    padding-top: 0;
    padding-bottom: 120px;
    padding-left: 0;
    padding-right: 0;
  }
}

/* Content wrapper for vertical centering */
.content-wrapper {
  min-height: 100%;
  width: 100%;
  flex-grow: 1;
  padding: 0px;
  margin: 0 auto;
}

@media (max-width: 480px) {
  .content-wrapper {
    padding: 16px;
    margin: 0;
  }
}

.order-subtotal-label {
  color: #4d4d4d;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
}

/* Fixed bottom buttons for small screens */
.fixed-bottom-buttons {
  position: fixed !important;
  bottom: 0 !important;
  left: 0 !important;
  right: 0 !important;
  z-index: 1000 !important;
  background: white !important;
  padding: 16px !important;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1) !important;
}

/* Use this to override Vuetify's internal styles for now */

.bottom-bar :deep(.v-toolbar__content) {
  min-height: 64px !important;
  height: auto !important;
}

/* More specific selector as fallback */
.v-app-bar.bottom-bar .v-toolbar__content {
  min-height: 64px !important;
  height: auto !important;
}

@media (max-width: 599px) {
  .bottom-bar :deep(.v-toolbar__content) {
    min-height: 100px !important;
    height: auto !important;
  }

  /* More specific selector as fallback */
  .v-app-bar.bottom-bar .v-toolbar__content {
    min-height: 100px !important;
    height: auto !important;
  }
}
</style>
